import { QueryKey, useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query';
import {
  EditAccountDto,
  KnownProjectTeamMemberDtoStatus,
  KnownSortOrder,
  ProjectTeamMemberDto,
  SendInvitationDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { QueryKeys } from '../../core/queries/query-keys.constants';
import { RestError } from '@azure/core-http';
import { useApi } from '../../core/api/api.context';
import { fetchAllPages } from '../../core/queries/query-helpers';

export interface UseQueryListProjectTeamMembersParams {
  sortBy: string;
  sortOrder: KnownSortOrder;
  status: KnownProjectTeamMemberDtoStatus | '';
  includeRemoved?: boolean;
}

export type TeamMembersListControlsState = UseQueryListProjectTeamMembersParams & {
  searchTerm?: string;
};

export const useQueryListProjectTeamMembers = (
  projectId: string,
  params?: Partial<UseQueryListProjectTeamMembersParams>,
  isEnabled = true
) => {
  const api = useApi();

  return useQuery(
    [QueryKeys.PROJECTS, projectId, QueryKeys.TEAM_MEMBERS, params],
    async () => {
      return await fetchAllPages(
        async (pageNo) =>
          await api.projectsController.teamMembersList(projectId, {
            ...(params || {}),
            pageNo,
            perPageRecords: 100
          })
      );
    },
    {
      keepPreviousData: true,
      enabled: isEnabled
    }
  );
};

export const useQueryGetProjectTeamMember = (
  projectId: string,
  teamMemberId: string,
  options?: Omit<
    UseQueryOptions<ProjectTeamMemberDto, RestError, ProjectTeamMemberDto, QueryKey>,
    'queryKey' | 'queryFn'
  >
) => {
  const api = useApi();

  return useQuery<ProjectTeamMemberDto, RestError>(
    [QueryKeys.PROJECTS, projectId, QueryKeys.TEAM_MEMBERS, teamMemberId],
    async () => {
      return await api.projectsController.teamMembersGet(projectId, teamMemberId);
    },
    options
  );
};

export const useMutationInviteTeamMember = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError, SendInvitationDto>(async (data: SendInvitationDto) => {
    await api.invitationsController.send(data);
    await queryClient.invalidateQueries([QueryKeys.ACCOUNTS]);
    if (data.accountProjectMembership?.projectID) {
      await queryClient.invalidateQueries([
        QueryKeys.PROJECTS,
        data.accountProjectMembership.projectID,
        QueryKeys.TEAM_MEMBERS
      ]);
    }
    for (const { projectID } of data.account?.projects ?? []) {
      await queryClient.invalidateQueries([QueryKeys.PROJECTS, projectID, QueryKeys.TEAM_MEMBERS]);
    }
  });
};

export function useMutationUpdateAccount(projectId: string, accountId: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(async (dto: EditAccountDto) => {
    await api.accountsController.edit(accountId, dto);
    await queryClient.invalidateQueries([QueryKeys.PROJECTS, projectId, QueryKeys.TEAM_MEMBERS]);
  });
}

export function useMutationUpdateTeamMember(userId?: string, projectId?: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(async (status: KnownProjectTeamMemberDtoStatus) => {
    if (!userId || !projectId) {
      return;
    }
    await api.accountsController.projectsEdit(userId, projectId, { status });
    await queryClient.invalidateQueries([QueryKeys.PROJECTS, projectId, QueryKeys.TEAM_MEMBERS]);
    await queryClient.invalidateQueries([QueryKeys.ACCOUNTS, userId, QueryKeys.TEAM_MEMBERS]);
  });
}

export const useMutationResendInvite = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError, { invitationID: string; projectID: string }>(
    async ({ invitationID, projectID }) => {
      await api.invitationsController.resend(invitationID, {
        projectID
      });
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.INVITATIONS);
      }
    }
  );
};
